import jsPDF from "jspdf";
import JSZip from "jszip";
import { PDFDocument } from "pdf-lib";
import { supabase } from "../../lib/supabase";
import { fetchInvoiceSettings } from "./invoiceEngine";
import { logActivity } from "./driveClient";

// ═══════════════════════════════════════════════════════════════
// Monatsabschluss / Auto-Grouping für Steuerberater
// Sammelt alle Rechnungen (Ein-/Ausgang) + Verträge eines Monats,
// baut eine Übersichts-PDF und packt alles zu einer ZIP.
// 100% clientseitig — nutzt Supabase Storage & jszip.
// ═══════════════════════════════════════════════════════════════

const PDF_LOGO_URL = "https://customer-assets-jai6qajn.emergentagent.net/job_neon-beats-label/artifacts/as4oc6zo_INDEPENDENT_MUSIC_LABEL__1_-removebg-preview.png";
let _logoCache = null;
async function loadLogoDataUrl() {
  if (_logoCache) return _logoCache;
  try {
    const resp = await fetch(PDF_LOGO_URL, { mode: "cors" });
    const blob = await resp.blob();
    const dataUrl = await new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.onerror = rej;
      r.readAsDataURL(blob);
    });
    _logoCache = dataUrl;
    return dataUrl;
  } catch {
    return null;
  }
}

function formatEUR(v) {
  return (Number(v || 0)).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
}

function sanitizeFileName(name) {
  return String(name || "").replace(/[^a-zA-Z0-9._-]+/g, "_").slice(0, 120);
}

// ─── Fetcher: alles was zum Monat gehört ─────────────────────
export async function fetchMonthData(month /* "YYYY-MM" */) {
  const [inv, con, mh] = await Promise.all([
    supabase
      .from("drive_invoices")
      .select("*, drive_artists(artist_name), drive_customers(name), drive_externals(full_name)")
      .eq("contract_month", month)
      .order("invoice_date", { ascending: true }),
    supabase
      .from("drive_contracts")
      .select("*, drive_artists(artist_name), drive_customers(name), drive_externals(full_name)")
      .eq("contract_month", month)
      .order("created_at", { ascending: true }),
    supabase
      .from("drive_music_hub_payouts")
      .select("*")
      .eq("month", month)
      .order("created_at", { ascending: true }),
  ]);
  if (inv.error) throw inv.error;
  if (con.error) throw con.error;
  // MusicHub-Fehler nicht fatal (Tabelle könnte noch fehlen)
  const musichub = mh.error ? [] : (mh.data || []);

  const outgoing = (inv.data || []).filter((i) => i.kind === "ausgang");
  const incoming = (inv.data || []).filter((i) => i.kind === "eingang");
  const contracts = con.data || [];

  const sum = (arr, key) => arr.reduce((s, x) => s + Number(x[key] || 0), 0);
  const musichubTotal = musichub.reduce((s, r) => s + Number(r.amount || 0), 0);
  const totals = {
    outgoing: {
      count: outgoing.length,
      subtotal: sum(outgoing, "subtotal"),
      tax: sum(outgoing, "tax_amount"),
      total: sum(outgoing, "total"),
    },
    incoming: {
      count: incoming.length,
      subtotal: sum(incoming, "subtotal"),
      tax: sum(incoming, "tax_amount"),
      total: sum(incoming, "total"),
    },
    musichub: {
      count: musichub.length,
      total: musichubTotal,
    },
    // Netto = (Ausgang + MusicHub) − Eingang
    net: sum(outgoing, "total") + musichubTotal - sum(incoming, "total"),
    contractCount: contracts.length,
  };

  return { outgoing, incoming, contracts, musichub, totals };
}

function recipientLabel(row) {
  return (
    row.drive_artists?.artist_name ||
    row.drive_customers?.name ||
    row.drive_externals?.full_name ||
    row.recipient_snapshot?.name ||
    "—"
  );
}

// ─── Übersichts-PDF ─────────────────────────────────────────
export async function renderOverviewPdf(month, data) {
  const pdf = new jsPDF({ unit: "mm", format: "a4" });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const marginX = 18;
  const bannerH = 40;
  const marginBottom = 22;
  const contentW = pageW - 2 * marginX;

  const INK = [24, 24, 28];
  const MUTED = [110, 110, 118];
  const LINE = [220, 220, 224];
  const BORDEAUX = [128, 24, 38];
  const ACCENT = [180, 30, 42];
  const SOFT = [245, 245, 247];

  const logoData = await loadLogoDataUrl();
  const settings = await fetchInvoiceSettings().catch(() => ({}));

  const drawBanner = () => {
    pdf.setFillColor(...BORDEAUX);
    pdf.rect(0, 0, pageW, bannerH, "F");
    if (logoData) {
      try {
        const lw = 76, lh = 30;
        pdf.addImage(logoData, "PNG", (pageW - lw) / 2, (bannerH - lh) / 2, lw, lh);
      } catch { /* ignore */ }
    }
  };

  const drawFooter = (page, total) => {
    const fy = pageH - 12;
    pdf.setDrawColor(...LINE);
    pdf.setLineWidth(0.25);
    pdf.line(marginX, fy - 4, pageW - marginX, fy - 4);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7);
    pdf.setTextColor(...MUTED);
    pdf.text(`${settings.company_name || "Straßenwert Records"} · Monatsabschluss ${month}`, marginX, fy);
    pdf.text(`Seite ${page} / ${total}`, pageW - marginX, fy, { align: "right" });
  };

  let y = bannerH + 14;
  drawBanner();

  // Titel
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(22);
  pdf.setCharSpace(1);
  pdf.setTextColor(...INK);
  pdf.text("MONATSABSCHLUSS", marginX, y);
  pdf.setCharSpace(0);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10);
  pdf.setTextColor(...MUTED);
  const [yr, mo] = month.split("-");
  const monthLabel = new Date(Number(yr), Number(mo) - 1, 1).toLocaleDateString("de-DE", { month: "long", year: "numeric" });
  pdf.text(monthLabel, marginX, y + 5.5);
  pdf.setDrawColor(...ACCENT);
  pdf.setLineWidth(0.9);
  pdf.line(marginX, y + 2, marginX + 42, y + 2);
  pdf.setLineWidth(0.2);
  y += 16;

  // Kurz-Summary Box
  pdf.setFillColor(...SOFT);
  pdf.rect(marginX, y, contentW, 26, "F");
  const col = contentW / 5;
  const boxItems = [
    { label: "Ausgangsrechnungen", val: `${data.totals.outgoing.count} Stk.`, sub: formatEUR(data.totals.outgoing.total) },
    { label: "Eingangsrechnungen", val: `${data.totals.incoming.count} Stk.`, sub: formatEUR(data.totals.incoming.total) },
    { label: "MusicHub", val: `${data.totals.musichub?.count || 0} Stk.`, sub: formatEUR(data.totals.musichub?.total || 0) },
    { label: "Verträge", val: `${data.totals.contractCount} Stk.`, sub: "" },
    { label: "Netto", val: formatEUR(data.totals.net), sub: "Ausgang+MH − Eingang" },
  ];
  boxItems.forEach((b, i) => {
    const x = marginX + col * i + 4;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7.5);
    pdf.setTextColor(...MUTED);
    pdf.text(b.label.toUpperCase(), x, y + 6);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(13);
    pdf.setTextColor(...INK);
    pdf.text(b.val, x, y + 14);
    if (b.sub) {
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(7.5);
      pdf.setTextColor(...MUTED);
      pdf.text(b.sub, x, y + 20);
    }
  });
  y += 34;

  const ensure = (need) => {
    if (y + need > pageH - marginBottom) {
      pdf.addPage();
      drawBanner();
      y = bannerH + 14;
    }
  };

  const section = (title) => {
    ensure(14);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(12);
    pdf.setTextColor(...INK);
    pdf.text(title, marginX, y);
    pdf.setDrawColor(...ACCENT);
    pdf.setLineWidth(0.7);
    pdf.line(marginX, y + 1.5, marginX + 24, y + 1.5);
    pdf.setLineWidth(0.2);
    y += 8;
  };

  const drawInvoiceTable = (rows) => {
    if (!rows.length) {
      pdf.setFont("helvetica", "italic");
      pdf.setFontSize(9);
      pdf.setTextColor(...MUTED);
      pdf.text("Keine Einträge in diesem Monat.", marginX, y);
      y += 8;
      return;
    }
    const cX = {
      nr: marginX,
      date: marginX + 30,
      partner: marginX + 55,
      net: pageW - marginX - 55,
      tax: pageW - marginX - 30,
      total: pageW - marginX,
    };
    ensure(10);
    pdf.setFillColor(...SOFT);
    pdf.rect(marginX, y - 4, contentW, 7, "F");
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(7.5);
    pdf.setTextColor(...INK);
    pdf.text("RE-NR.", cX.nr, y);
    pdf.text("DATUM", cX.date, y);
    pdf.text("PARTNER", cX.partner, y);
    pdf.text("NETTO", cX.net, y, { align: "right" });
    pdf.text("USt.", cX.tax, y, { align: "right" });
    pdf.text("GESAMT", cX.total, y, { align: "right" });
    y += 5;

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(...INK);
    let stripe = false;
    for (const r of rows) {
      ensure(7);
      if (stripe) {
        pdf.setFillColor(250, 250, 251);
        pdf.rect(marginX, y - 3.5, contentW, 6, "F");
      }
      stripe = !stripe;
      pdf.setTextColor(...INK);
      pdf.text(String(r.invoice_number || "—").slice(0, 14), cX.nr, y);
      pdf.text(r.invoice_date ? new Date(r.invoice_date).toLocaleDateString("de-DE") : "—", cX.date, y);
      const partner = String(recipientLabel(r)).slice(0, 32);
      pdf.text(partner, cX.partner, y);
      pdf.text(formatEUR(r.subtotal), cX.net, y, { align: "right" });
      pdf.text(formatEUR(r.tax_amount), cX.tax, y, { align: "right" });
      pdf.setFont("helvetica", "bold");
      pdf.text(formatEUR(r.total), cX.total, y, { align: "right" });
      pdf.setFont("helvetica", "normal");
      y += 6;
    }
    // Summen-Zeile
    ensure(9);
    pdf.setDrawColor(...INK);
    pdf.setLineWidth(0.4);
    pdf.line(marginX, y - 1, pageW - marginX, y - 1);
    y += 4;
    const totals = rows.reduce((s, r) => ({
      sub: s.sub + Number(r.subtotal || 0),
      tax: s.tax + Number(r.tax_amount || 0),
      tot: s.tot + Number(r.total || 0),
    }), { sub: 0, tax: 0, tot: 0 });
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9.5);
    pdf.setTextColor(...INK);
    pdf.text("Summe", cX.partner, y);
    pdf.text(formatEUR(totals.sub), cX.net, y, { align: "right" });
    pdf.text(formatEUR(totals.tax), cX.tax, y, { align: "right" });
    pdf.setTextColor(...ACCENT);
    pdf.text(formatEUR(totals.tot), cX.total, y, { align: "right" });
    pdf.setTextColor(...INK);
    y += 10;
  };

  const drawContractTable = (rows) => {
    if (!rows.length) {
      pdf.setFont("helvetica", "italic");
      pdf.setFontSize(9);
      pdf.setTextColor(...MUTED);
      pdf.text("Keine Verträge in diesem Monat.", marginX, y);
      y += 8;
      return;
    }
    const cX = {
      date: marginX,
      title: marginX + 30,
      partner: marginX + 100,
      status: pageW - marginX,
    };
    ensure(10);
    pdf.setFillColor(...SOFT);
    pdf.rect(marginX, y - 4, contentW, 7, "F");
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(7.5);
    pdf.setTextColor(...INK);
    pdf.text("DATUM", cX.date, y);
    pdf.text("TITEL", cX.title, y);
    pdf.text("PARTNER", cX.partner, y);
    pdf.text("STATUS", cX.status, y, { align: "right" });
    y += 5;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    let stripe = false;
    for (const r of rows) {
      ensure(7);
      if (stripe) {
        pdf.setFillColor(250, 250, 251);
        pdf.rect(marginX, y - 3.5, contentW, 6, "F");
      }
      stripe = !stripe;
      pdf.setTextColor(...INK);
      pdf.text(new Date(r.created_at).toLocaleDateString("de-DE"), cX.date, y);
      pdf.text(String(r.title || "—").slice(0, 42), cX.title, y);
      pdf.text(String(recipientLabel(r)).slice(0, 24), cX.partner, y);
      pdf.setTextColor(...(r.status === "signed" ? ACCENT : MUTED));
      pdf.text((r.status || "draft").toUpperCase(), cX.status, y, { align: "right" });
      pdf.setTextColor(...INK);
      y += 6;
    }
    y += 4;
  };

  const drawMusicHubTable = (rows) => {
    if (!rows.length) {
      pdf.setFont("helvetica", "italic");
      pdf.setFontSize(9);
      pdf.setTextColor(...MUTED);
      pdf.text("Keine MusicHub-Abrechnungen in diesem Monat.", marginX, y);
      y += 8;
      return;
    }
    const cX = {
      date: marginX,
      title: marginX + 30,
      notes: marginX + 100,
      amount: pageW - marginX,
    };
    ensure(10);
    pdf.setFillColor(...SOFT);
    pdf.rect(marginX, y - 4, contentW, 7, "F");
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(7.5);
    pdf.setTextColor(...INK);
    pdf.text("DATUM", cX.date, y);
    pdf.text("BEZEICHNUNG", cX.title, y);
    pdf.text("NOTIZEN", cX.notes, y);
    pdf.text("BETRAG", cX.amount, y, { align: "right" });
    y += 5;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    let stripe = false;
    let sum = 0;
    for (const r of rows) {
      ensure(7);
      if (stripe) {
        pdf.setFillColor(250, 250, 251);
        pdf.rect(marginX, y - 3.5, contentW, 6, "F");
      }
      stripe = !stripe;
      sum += Number(r.amount || 0);
      pdf.setTextColor(...INK);
      pdf.text(new Date(r.created_at).toLocaleDateString("de-DE"), cX.date, y);
      pdf.text(String(r.title || "—").slice(0, 32), cX.title, y);
      pdf.text(String(r.notes || "").slice(0, 30), cX.notes, y);
      pdf.setFont("helvetica", "bold");
      pdf.text(formatEUR(r.amount), cX.amount, y, { align: "right" });
      pdf.setFont("helvetica", "normal");
      y += 6;
    }
    ensure(9);
    pdf.setDrawColor(...INK);
    pdf.setLineWidth(0.4);
    pdf.line(marginX, y - 1, pageW - marginX, y - 1);
    y += 4;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9.5);
    pdf.setTextColor(...INK);
    pdf.text("Summe", cX.notes, y);
    pdf.setTextColor(...ACCENT);
    pdf.text(formatEUR(sum), cX.amount, y, { align: "right" });
    pdf.setTextColor(...INK);
    y += 10;
  };

  section("Ausgangsrechnungen");
  drawInvoiceTable(data.outgoing);
  section("Eingangsrechnungen");
  drawInvoiceTable(data.incoming);
  section("MusicHub-Abrechnungen");
  drawMusicHubTable(data.musichub || []);
  section("Verträge");
  drawContractTable(data.contracts);

  // Footer auf allen Seiten
  const totalPages = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i);
    drawFooter(i, totalPages);
  }
  return pdf;
}

// ─── ZIP-Export: alle PDFs + Übersicht ────────────────────────
export async function buildMonthZip(month, data, onProgress) {
  const zip = new JSZip();
  const root = zip.folder(`Monatsabschluss-${month}`);

  // 1) Merged Mega-PDF (Übersicht + alle Originale) als Deckblatt
  onProgress?.("Zusammengesetztes PDF wird erstellt…");
  const mergedBlob = await buildMergedPdf(month, data, onProgress);
  root.file(`0_Monatsabschluss-${month}-KOMPLETT.pdf`, mergedBlob);

  // 2) Ausgangsrechnungen (Original-Dateien)
  const outFolder = root.folder("1_Ausgangsrechnungen");
  for (let i = 0; i < data.outgoing.length; i++) {
    const inv = data.outgoing[i];
    onProgress?.(`Ausgangsrechnung ${i + 1}/${data.outgoing.length}…`);
    if (!inv.storage_path) continue;
    try {
      const { data: blob, error } = await supabase.storage.from("drive-files").download(inv.storage_path);
      if (error || !blob) continue;
      const name = sanitizeFileName(`Rechnung-${inv.invoice_number}-${recipientLabel(inv)}`) + ".pdf";
      outFolder.file(name, blob);
    } catch { /* skip */ }
  }

  // 3) Eingangsrechnungen
  const inFolder = root.folder("2_Eingangsrechnungen");
  for (let i = 0; i < data.incoming.length; i++) {
    const inv = data.incoming[i];
    onProgress?.(`Eingangsrechnung ${i + 1}/${data.incoming.length}…`);
    if (!inv.storage_path) continue;
    try {
      const { data: blob, error } = await supabase.storage.from("drive-files").download(inv.storage_path);
      if (error || !blob) continue;
      const ext = inv.storage_path.split(".").pop() || "pdf";
      const name = sanitizeFileName(`Eingang-${inv.invoice_number}-${recipientLabel(inv)}`) + "." + ext;
      inFolder.file(name, blob);
    } catch { /* skip */ }
  }

  // 4) MusicHub-Abrechnungen
  const mhFolder = root.folder("3_MusicHub");
  const musichub = data.musichub || [];
  for (let i = 0; i < musichub.length; i++) {
    const r = musichub[i];
    onProgress?.(`MusicHub ${i + 1}/${musichub.length}…`);
    if (!r.storage_path) continue;
    try {
      const { data: blob, error } = await supabase.storage.from("drive-files").download(r.storage_path);
      if (error || !blob) continue;
      const ext = (r.file_name || r.storage_path).split(".").pop() || "pdf";
      const name = sanitizeFileName(`${r.title || "MusicHub"}`) + "." + ext;
      mhFolder.file(name, blob);
    } catch { /* skip */ }
  }

  // 5) Verträge (signed_storage_path bevorzugt)
  const conFolder = root.folder("4_Vertraege");
  for (let i = 0; i < data.contracts.length; i++) {
    const c = data.contracts[i];
    onProgress?.(`Vertrag ${i + 1}/${data.contracts.length}…`);
    const path = c.signed_storage_path || c.storage_path;
    if (!path) continue;
    try {
      const { data: blob, error } = await supabase.storage.from("drive-files").download(path);
      if (error || !blob) continue;
      const name = sanitizeFileName(`${c.title || "Vertrag"}${c.signed_storage_path ? "-SIGNED" : ""}`) + ".pdf";
      conFolder.file(name, blob);
    } catch { /* skip */ }
  }

  onProgress?.("ZIP wird gepackt…");
  const zipBlob = await zip.generateAsync({ type: "blob", compression: "DEFLATE" });
  await logActivity("month_export", { type: "month_export", name: month });
  return zipBlob;
}

// ═══════════════════════════════════════════════════════════════
// Merged Mega-PDF: Deckblatt (Übersicht) + alle Originale
// ═══════════════════════════════════════════════════════════════
async function tryAppendPdfFromStorage(mergedDoc, storagePath) {
  if (!storagePath) return false;
  try {
    const { data: blob, error } = await supabase.storage.from("drive-files").download(storagePath);
    if (error || !blob) return false;
    // Nur PDFs mergen — andere Dateitypen skippen
    const buf = await blob.arrayBuffer();
    // Simple magic-byte check: PDF starts with %PDF
    const head = new Uint8Array(buf.slice(0, 4));
    if (head[0] !== 0x25 || head[1] !== 0x50 || head[2] !== 0x44 || head[3] !== 0x46) return false;
    const src = await PDFDocument.load(buf, { ignoreEncryption: true });
    const pages = await mergedDoc.copyPages(src, src.getPageIndices());
    pages.forEach((p) => mergedDoc.addPage(p));
    return true;
  } catch (e) {
    console.warn("PDF-Merge fehlgeschlagen für", storagePath, e);
    return false;
  }
}

export async function buildMergedPdf(month, data, onProgress) {
  onProgress?.("Deckblatt wird erstellt…");
  const cover = await renderOverviewPdf(month, data);
  const coverBytes = cover.output("arraybuffer");
  const merged = await PDFDocument.load(coverBytes);

  // 1) Ausgangsrechnungen
  for (let i = 0; i < data.outgoing.length; i++) {
    onProgress?.(`Ausgangsrechnung ${i + 1}/${data.outgoing.length} wird eingefügt…`);
    await tryAppendPdfFromStorage(merged, data.outgoing[i].storage_path);
  }

  // 2) Eingangsrechnungen
  for (let i = 0; i < data.incoming.length; i++) {
    onProgress?.(`Eingangsrechnung ${i + 1}/${data.incoming.length} wird eingefügt…`);
    await tryAppendPdfFromStorage(merged, data.incoming[i].storage_path);
  }

  // 3) MusicHub
  const musichub = data.musichub || [];
  for (let i = 0; i < musichub.length; i++) {
    onProgress?.(`MusicHub ${i + 1}/${musichub.length} wird eingefügt…`);
    await tryAppendPdfFromStorage(merged, musichub[i].storage_path);
  }

  // 4) Verträge
  for (let i = 0; i < data.contracts.length; i++) {
    onProgress?.(`Vertrag ${i + 1}/${data.contracts.length} wird eingefügt…`);
    const c = data.contracts[i];
    await tryAppendPdfFromStorage(merged, c.signed_storage_path || c.storage_path);
  }

  onProgress?.("Finalisiere PDF…");
  const bytes = await merged.save();
  return new Blob([bytes], { type: "application/pdf" });
}

// Convenience-Downloader
export function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ═══════════════════════════════════════════════════════════════
// Archiv: Monatsabschluss speichern / laden / löschen
// ═══════════════════════════════════════════════════════════════

export async function saveMonthClosing(month, data, onProgress) {
  onProgress?.("Zusammengesetztes PDF wird erstellt…");
  const pdfBlob = await buildMergedPdf(month, data, onProgress);

  onProgress?.("ZIP wird gebaut…");
  const zipBlob = await buildMonthZip(month, data, onProgress);

  onProgress?.("Speichern in Archiv…");
  const ts = Date.now();
  const zipPath = `month-closings/${month}/${ts}-Monatsabschluss-${month}.zip`;
  const pdfPath = `month-closings/${month}/${ts}-Monatsabschluss-${month}-KOMPLETT.pdf`;

  const upZip = await supabase.storage.from("drive-files").upload(zipPath, zipBlob, { contentType: "application/zip", upsert: false });
  if (upZip.error) throw upZip.error;

  const upPdf = await supabase.storage.from("drive-files").upload(pdfPath, pdfBlob, { contentType: "application/pdf", upsert: false });
  if (upPdf.error) throw upPdf.error;

  const { data: row, error } = await supabase.from("drive_month_closings").insert([{
    month,
    zip_storage_path: zipPath,
    pdf_storage_path: pdfPath,
    outgoing_count: data.totals.outgoing.count,
    outgoing_total: data.totals.outgoing.total,
    incoming_count: data.totals.incoming.count,
    incoming_total: data.totals.incoming.total,
    contract_count: data.totals.contractCount,
    net_total: data.totals.net,
  }]).select().single();
  if (error) throw error;

  await logActivity("save_month_closing", { type: "month_closing", id: row.id, name: month });
  return row;
}

export async function fetchMonthClosings() {
  const { data, error } = await supabase
    .from("drive_month_closings")
    .select("*")
    .order("month", { ascending: false })
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function downloadStoredClosing(path, filename) {
  const { data: blob, error } = await supabase.storage.from("drive-files").download(path);
  if (error) throw error;
  triggerDownload(blob, filename);
}

export async function deleteMonthClosing(row) {
  const paths = [row.zip_storage_path, row.pdf_storage_path].filter(Boolean);
  if (paths.length) {
    await supabase.storage.from("drive-files").remove(paths).catch(() => {});
  }
  const { error } = await supabase.from("drive_month_closings").delete().eq("id", row.id);
  if (error) throw error;
  await logActivity("delete_month_closing", { type: "month_closing", id: row.id, name: row.month });
}
